import { Outlet, useOutletContext, useParams } from 'react-router-dom'

import { WorkScheduleEditPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import WorkScheduleSettings from 'modules/work_schedules/components/WorkScheduleSettings'
import type { RouteContextParams } from 'modules/work_schedules/lib/RouteContextParams'
import { RedirectToWorkScheduleListPage } from 'modules/work_schedules/pages/settings/RedirectToWorkScheduleListPage'

const WorkScheduleEditPage = () => {
  const {
    back,
    workSchedule,
    isNotFound,
    defaultPeriod,
    overlapPeriods,
    dayConfigurations,
    loading,
  } = useOutletContext<RouteContextParams>()

  const { id } = useParams()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading: loadingGraphql } = useQuery(WorkScheduleEditPageDocument, {
    variables: {
      scheduleId: Number(id),
      companyId,
    },
  })

  const canAssignEmployees = data?.workSchedule.schedule?.canAssignEmployeesPolicy.can ?? false
  const canReassignAll = data?.workSchedule.schedule?.canReassignEmployeesPolicy.can ?? false
  const features = data?.features.featuresConnection.nodes ?? []
  const canAddBreaks =
    features.find((feature) => feature.name === 'time_settings_breaks')?.enabled ?? false
  const hasFixedWorkSchedulesEnabled =
    features.find((feature) => feature.name === 'dev_fixed_work_schedules')?.enabled ?? false

  if (loading || loadingGraphql) {
    return <Loading />
  }

  if (!hasFixedWorkSchedulesEnabled && defaultPeriod?.isFixed) {
    return <RedirectToWorkScheduleListPage />
  }

  return (
    <WorkScheduleSettings
      workSchedule={workSchedule}
      dayConfigurations={dayConfigurations}
      periodLoading={loading}
      defaultPeriod={defaultPeriod}
      canAssignEmployees={canAssignEmployees}
      canReassignAll={canReassignAll}
      canAddBreaks={canAddBreaks}
    >
      <Outlet
        context={{
          back,
          workSchedule,
          isNotFound,
          defaultPeriod,
          overlapPeriods,
          dayConfigurations,
          loading,
        }}
      />
    </WorkScheduleSettings>
  )
}

export default observer(WorkScheduleEditPage)
